import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { emailVerificationService } from "../services/emailVerificationService";
import { emailVerificationDisplayId } from "./EmailVerificationCommonStore";
import { EmailVerification } from "../../../api/graphql";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { EmailVerificationStatus } from "../../../api/graphql";
import { EmailVerificationScope } from "../../../api/graphql";
import { userService } from "../../user/services/userService";
import { InitializeParams } from "../../../store/types";

export class EmailVerificationViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = emailVerificationService;
  emailVerificationInstance: EmailVerification | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  emailValue: string = "";
  statusValue: EmailVerificationStatus = "" as EmailVerificationStatus;
  scopeValue: EmailVerificationScope | "" = "" as EmailVerificationScope | "";
  expiresAtValue: Date | null = null;
  sentAtValue: Date | null = null;
  verifiedAtValue: Date | null = null;
  sentCountValue: number = 0;
  lastSentAtValue: Date | null = null;
  requestedByService = userService;
  requestedBySearchValue: string = "";
  requestedByIdValue: string = "";
  requestedBySelectedValue: { displayId: string; id: string } | null = null;
  sourceEntityValue: string = "";
  sourceRecordIdValue: string = "";
  validationChangesValue: string = "{}";
  validationSubmittedAtValue: Date | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEmailVerificationById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.emailVerificationOne({ id });
    this.emailVerificationInstance = result.emailVerificationOne;
    if (this.emailVerificationInstance) {
      this.loadValues(this.emailVerificationInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.emailVerificationInstance) {
      return emailVerificationDisplayId(
        this.emailVerificationInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EmailVerificationSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "requestedBy") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.userOne({ id: params.returnData.resultId });
        const user = result.userOne;
        this.requestedBySelectedValue = user;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getStatusOptions() {
    return this.langStore.EmailVerificationStatusOptions;
  }

  getScopeOptions() {
    return this.langStore.EmailVerificationScopeOptions;
  }

  get requestedByValue() {
    if (this.requestedBySelectedValue) {
      return {
        id: this.requestedBySelectedValue.id,
        displayId: userDisplayId(this.requestedBySelectedValue, this.langStore),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.emailVerificationInstance = null;
    this.emailValue = "";
    this.statusValue = "" as EmailVerificationStatus;
    this.scopeValue = "" as EmailVerificationScope | "";
    this.expiresAtValue = null;
    this.sentAtValue = null;
    this.verifiedAtValue = null;
    this.sentCountValue = 0;
    this.lastSentAtValue = null;
    this.requestedBySearchValue = "";
    this.requestedByIdValue = "";
    this.sourceEntityValue = "";
    this.sourceRecordIdValue = "";
    this.validationChangesValue = "{}";
    this.validationSubmittedAtValue = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEmailVerification() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("emailVerification_update");
  }

  editEmailVerification() {
    const id = this.emailVerificationInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/emailVerification/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEmailVerification(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEmailVerification(): any {
    yield this.close();
  }

  loadValues(instance: EmailVerification) {
    this.idValue = instance.id;
    this.emailValue = instance.email;
    this.statusValue = instance.status;
    this.scopeValue = instance.scope || ("" as EmailVerificationScope | "");
    this.expiresAtValue = new Date(instance.expiresAt);
    this.sentAtValue = instance.sentAt ? new Date(instance.sentAt) : null;
    this.verifiedAtValue = instance.verifiedAt
      ? new Date(instance.verifiedAt)
      : null;
    this.sentCountValue = instance.sentCount;
    this.lastSentAtValue = instance.lastSentAt
      ? new Date(instance.lastSentAt)
      : null;
    this.requestedBySelectedValue = instance.requestedBy;
    this.sourceEntityValue = instance.sourceEntity || "";
    this.sourceRecordIdValue = instance.sourceRecordId || "";
    this.validationChangesValue = instance.validationChanges || "";
    this.validationSubmittedAtValue = instance.validationSubmittedAt
      ? new Date(instance.validationSubmittedAt)
      : null;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEmailVerificationById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
